import React, { useState, useEffect, useRef, useMemo, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import Homepage from '../Homepage';
import TripOverview from '../TripOverview';
import { useTrips } from '../hooks/useTrips';
import { API_BASE_URL } from '../utils/config';
import LanguageSwitch from '../components/LanguageSwitch';
import { DEMO_TRIP_ID, buildDemoTrip } from '../utils/demoTrip';

// Chunk separato: chi entra con token non scarica landing né framer-motion
const Landing = lazy(() => import('../Landing'));

// ─── URL helpers ──────────────────────────────────────────────────────────────

function parseUrl() {
    const parts = window.location.pathname.split('/').filter(Boolean);
    // /trip/1  or  /trip/1/city/Tokyo  (o /trip/demo per il viaggio dimostrativo)
    if (parts[0] !== 'trip' || !parts[1]) return { tripId: null, cityName: null };
    return {
        tripId:   parts[1] === DEMO_TRIP_ID ? DEMO_TRIP_ID : parseInt(parts[1], 10),
        cityName: parts[2] === 'city' && parts[3] ? decodeURIComponent(parts[3]) : null,
    };
}

const STORAGE_TOKEN_KEY = 'travel_trips_token';

// Preview mode: ?from=portfolio, oppure /trip/demo senza sessione
function isPreviewUrl() {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('from') === 'portfolio') return true;
        return parseUrl().tripId === DEMO_TRIP_ID && !params.get('token') && !localStorage.getItem(STORAGE_TOKEN_KEY);
    } catch { return false; }
}

function getTokenExp(token) {
    try {
        const [b64] = token.split('.');
        const payload = JSON.parse(atob(b64.replace(/-/g, '+').replace(/_/g, '/')));
        return payload.exp ?? null;
    } catch { return null; }
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function Modal({ children, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-8 border border-gray-200 dark:border-gray-700">
                {onClose && (
                    <button onClick={onClose} aria-label="Close" className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
                {children}
            </div>
        </div>
    );
}

function PasswordModal({ title, description, value, onChange, onSubmit, loading, error, onClose }) {
    const { t } = useTranslation();
    return (
        <Modal onClose={onClose}>
            <div className="text-center mb-6">
                <div className="text-5xl mb-3">🔒</div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{title}</h2>
                {description && <p className="text-gray-600 dark:text-gray-400 mt-2 text-sm">{description}</p>}
            </div>
            {error && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
                    {error}
                </div>
            )}
            {loading && (
                <div className="mb-4 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm">
                    {t('auth.loading')}
                </div>
            )}
            <input
                type="password"
                placeholder="Password"
                value={value}
                onChange={e => onChange(e.target.value)}
                onKeyPress={e => e.key === 'Enter' && !loading && onSubmit()}
                disabled={loading}
                className="input mb-4"
                autoFocus
            />
            <div className="flex justify-end">
                <button onClick={onSubmit} disabled={loading || !value.trim()} className="btn-primary">
                    {loading ? t('auth.submitting') : t('auth.submit')}
                </button>
            </div>
        </Modal>
    );
}

function DarkModeToggle({ isDark, onToggle }) {
    const { t } = useTranslation();
    return (
        <button
            onClick={onToggle}
            className="px-4 py-2 rounded-xl border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 font-semibold text-sm hover:border-primary-500 hover:text-primary-700 dark:hover:border-primary-500 dark:hover:text-primary-400 transition-all duration-200 shadow-md"
        >
            {isDark ? t('common.lightMode') : t('common.darkMode')}
        </button>
    );
}

// ─── App ──────────────────────────────────────────────────────────────────────

export default function TravelItineraryApp() {
    const { t, i18n } = useTranslation();
    const [selectedTrip, setSelectedTrip] = useState(null);
    const [navCityName, setNavCityName]   = useState(null); // city da auto-selezionare in TripOverview
    // Inizializzato in modo sincrono per non mostrare la landing per un frame con ?from=portfolio
    const [previewMode, setPreviewMode]   = useState(() => (isPreviewUrl() ? 'portfolio' : null));
    const [pathname, setPathname]         = useState(window.location.pathname);
    const [loginRequested, setLoginRequested] = useState(false); // modale password sopra la landing
    const tripsRef = useRef([]);

    const navigate = (url) => {
        window.history.pushState(null, '', url);
        setPathname(window.location.pathname);
    };

    const [sessionToken, setSessionToken] = useState(() => {
        try {
            const params   = new URLSearchParams(window.location.search);
            const urlToken = params.get('token');
            if (urlToken) {
                const clean = new URL(window.location.href);
                clean.searchParams.delete('token');
                window.history.replaceState({}, '', clean.toString());
                return urlToken;
            }
            return localStorage.getItem(STORAGE_TOKEN_KEY) || '';
        } catch { return ''; }
    });

    const [startupPassword, setStartupPassword]   = useState('');
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [authError, setAuthError]               = useState(null);
    const [showStartupModal, setIsStartupDialogOpen] = useState(true);
    // Sessione chiusa (cambio password, 401): riapri il modale invece di lasciare solo la landing
    const clearOrSetToken = useCallback((token) => {
        setSessionToken(token);
        if (!token) setLoginRequested(true);
    }, []);

    const [isDarkMode, setIsDarkMode] = useState(
        () => { try { return JSON.parse(localStorage.getItem('darkMode')) || false; } catch { return false; } }
    );

    useEffect(() => {
        try {
            if (sessionToken) localStorage.setItem(STORAGE_TOKEN_KEY, sessionToken);
            else              localStorage.removeItem(STORAGE_TOKEN_KEY);
        } catch {}
    }, [sessionToken]);

    useEffect(() => {
        isDarkMode ? document.documentElement.classList.add('dark')
                   : document.documentElement.classList.remove('dark');
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleDarkMode = () => {
        const next = !isDarkMode;
        next ? document.documentElement.classList.add('dark') : document.documentElement.classList.remove('dark');
        setIsDarkMode(next);
        localStorage.setItem('darkMode', JSON.stringify(next));
    };

    useEffect(() => {
        try {
            if (new URLSearchParams(window.location.search).get('from') === 'portfolio') {
                setPreviewMode('portfolio');
                setSessionToken('');
            }
        } catch {}
    }, []);

    // Feature 1: refresh silenzioso se token scade entro 7 giorni
    useEffect(() => {
        if (!sessionToken || previewMode) return;
        const exp = getTokenExp(sessionToken);
        if (!exp || exp - Date.now() > 7 * 24 * 60 * 60 * 1000) return;

        fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${sessionToken}` },
        })
            .then(res => res.ok ? res.json() : null)
            .then(data => { if (data?.token) setSessionToken(data.token); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const { trips, loading, error, isPreview, isOffline } = useTrips(
        sessionToken,
        setIsStartupDialogOpen,
        clearOrSetToken,
        previewMode,
    );

    // In preview il viaggio demo (locale, fittizio) precede quelli dell'API
    const demoTrip     = useMemo(() => buildDemoTrip(i18n.language), [i18n.language]);
    const visibleTrips = useMemo(
        () => (previewMode === 'portfolio' ? [demoTrip, ...trips] : trips),
        [previewMode, demoTrip, trips],
    );
    const shownTrip = selectedTrip?.id === DEMO_TRIP_ID ? demoTrip : selectedTrip;

    // Tieni tripsRef aggiornato per il popstate handler (che non ha accesso al closure)
    useEffect(() => { tripsRef.current = visibleTrips; }, [visibleTrips]);

    // Al caricamento dei trip, auto-seleziona trip (e città) se l'URL lo indica
    useEffect(() => {
        if (!visibleTrips.length || selectedTrip) return;
        const { tripId, cityName } = parseUrl();
        if (!tripId) return;
        const trip = visibleTrips.find(t => t.id === tripId);
        if (trip) {
            setSelectedTrip(trip);
            setNavCityName(cityName);
        }
    }, [visibleTrips]); // eslint-disable-line react-hooks/exhaustive-deps

    // Browser back/forward: aggiorna stato in base all'URL
    useEffect(() => {
        const onPop = () => {
            setPathname(window.location.pathname);
            // Indietro/avanti tra landing e anteprima: lo stato segue l'URL
            setPreviewMode(isPreviewUrl() ? 'portfolio' : null);
            const { tripId, cityName } = parseUrl();
            if (!tripId) {
                setSelectedTrip(null);
                setNavCityName(null);
            } else {
                const trip = tripsRef.current.find(t => t.id === tripId);
                if (trip) { setSelectedTrip(trip); setNavCityName(cityName); }
            }
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const handleStartupSubmit = async () => {
        if (!startupPassword.trim()) return;
        setIsAuthenticating(true);
        setAuthError(null);
        try {
            const res = await fetch(`${API_BASE_URL}/api/auth/trip`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify({ password: startupPassword }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setAuthError(data.message || t('auth.errorInvalidPassword'));
                return;
            }
            const { token } = await res.json();
            setSessionToken(token);
            setStartupPassword('');
            setAuthError(null);
        } catch {
            setAuthError(t('auth.errorConnection'));
        } finally {
            setIsAuthenticating(false);
        }
    };

    const handleSelectTrip = (trip) => {
        if (!sessionToken && !trip.cities) {
            // Preview mode without full trip data — prompt login instead of showing a 401 error
            setPreviewMode(null);
            setIsStartupDialogOpen(true);
            return;
        }
        setSelectedTrip(trip);
        setNavCityName(null);
        navigate(`/trip/${trip.id}`);
    };
    const handleBack = () => {
        setSelectedTrip(null);
        setNavCityName(null);
        navigate(previewMode ? '/?from=portfolio' : '/');
    };

    const exitPreview = () => {
        setPreviewMode(null);
        setSessionToken('');
        setSelectedTrip(null);
        setIsStartupDialogOpen(true);
        setLoginRequested(true);
        navigate('/');
    };

    const startPreview = () => {
        navigate('/?from=portfolio');
        setPreviewMode('portfolio');
        window.scrollTo(0, 0);
    };

    const closeLogin = () => {
        setLoginRequested(false);
        setAuthError(null);
        setStartupPassword('');
    };

    const needsAuth = !sessionToken && !previewMode;
    const isReady   = !!(sessionToken || previewMode);
    const onLanding = needsAuth && pathname === '/';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col transition-colors duration-200">

            {/* ── Banner offline ── */}
            {isOffline && (
                <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-700 px-4 py-2 text-center text-sm text-amber-800 dark:text-amber-300 font-medium">
                    {t('offline.banner')}
                </div>
            )}

            {/* ── Startup modal ── */}
            {/* ── Landing (/, senza sessione né preview) ── */}
            {onLanding && (
                <Suspense fallback={null}>
                    <Landing
                        onPreview={startPreview}
                        onLogin={() => setLoginRequested(true)}
                        isDark={isDarkMode}
                        onToggleDark={toggleDarkMode}
                    />
                </Suspense>
            )}

            {(needsAuth || isAuthenticating) && showStartupModal && (!onLanding || loginRequested) && (
                <PasswordModal
                    title={t('auth.title')}
                    description={t('auth.description')}
                    value={startupPassword}
                    onChange={setStartupPassword}
                    onSubmit={handleStartupSubmit}
                    loading={isAuthenticating}
                    error={authError || error}
                    onClose={onLanding ? closeLogin : undefined}
                />
            )}

            {/* ── Main content ── */}
            {isReady && (
                <div className="flex-1 pb-20">
                    {!selectedTrip ? (
                        <Homepage
                            onSelectTrip={handleSelectTrip}
                            tripsData={{ trips: visibleTrips }}
                            setSessionToken={clearOrSetToken}
                            sessionToken={sessionToken}
                            isPreview={isPreview}
                            loading={loading}
                        />
                    ) : (
                        <TripOverview
                            trip={shownTrip}
                            token={sessionToken}
                            onBack={handleBack}
                            initialCityName={navCityName}
                        />
                    )}
                </div>
            )}

            {isReady && !loading && visibleTrips.length === 0 && !error && (
                <div className="flex-1 flex items-center justify-center">
                    <p className="text-gray-600 dark:text-gray-400">{t('homepage.noTrips')}</p>
                </div>
            )}

            {/* ── Bottom-right: preview badge + dark mode + language switch (sulla landing sono nell'header) ── */}
            {!onLanding && <div className="fixed bottom-4 right-4 z-10 flex items-end gap-2">
                {isPreview && (
                    <div className="flex items-center gap-2">
                        <span className="px-3 py-2 rounded-lg text-sm font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                            {t('preview.badge')}
                        </span>
                        <button
                            onClick={exitPreview}
                            className="px-3 py-2 rounded-lg text-sm font-semibold border-2 border-blue-300 dark:border-blue-600 text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors shadow-md"
                        >
                            {t('preview.login')}
                        </button>
                    </div>
                )}
                <LanguageSwitch />
                <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
            </div>}
        </div>
    );
}
